import { DateTime } from "luxon";
import React, { useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import ReactJson from "react-json-view";
import { useImmer, useImmerReducer } from "use-immer";

import { IgPaper, IgTypography } from "@intelligems/ig-theme";
import {
  Badge,
  Box,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";

import { useGetOrg } from "../../api/organizations/get-organization";
import { useConfigCompareQueries } from "../../api/plugin-configs/get-config-compare";
import { useGetConfigEvents } from "../../api/plugin-configs/get-config-events";
import { ConfigEventType } from "../../types/plugin";
import Loading from "../../utils/Loading";

import { EventFilter } from "./EventFilter";
import { EventPanel } from "./EventPanel";
import { filterByExperience } from "./utils";

export type FilterReduceState = {
  value: string;
  startTime: DateTime | null;
  endTime: DateTime | null;
};

export type FilterReducerAction =
  | { type: "setValue"; value: string }
  | { type: "reset" }
  | { type: "setStartTime"; value: DateTime | null }
  | {
      type: "setEndTime";
      value: DateTime | null;
    };

const filterReducer = (
  state: FilterReduceState,
  action: FilterReducerAction
) => {
  switch (action.type) {
    case "setValue":
      state.value = action.value;
      break;
    case "setStartTime":
      state.startTime = action.value;
      break;
    case "setEndTime":
      state.endTime = action.value;
      break;
    case "reset":
      state.value = "";
      state.startTime = null;
      state.endTime = null;
      break;
    default:
      break;
  }
};

const Title = () => {
  return (
    <Grid item>
      <Badge badgeContent={"BETA"} color={"primary"}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "26px",
          }}
          variant="h6"
        >
          VIEW CONFIGURATION HISTORY
        </Typography>
      </Badge>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "16px",
        }}
        variant="h6"
      >
        Compare your Intelligems configuration history
      </Typography>
    </Grid>
  );
};
type CompareEvents = {
  from: { key: string; ts: number };
  to: { key: string; ts: number };
} | null;
const Fallback = ({
  error,
  resetErrorBoundary,
  compareEvents,
}: {
  error: any;
  resetErrorBoundary: any;
  compareEvents: CompareEvents;
}) => {
  return (
    <Grid
      container
      item
      direction={"column"}
      alignItems={"center"}
      rowSpacing={2}
    >
      <Grid item>
        <IgTypography>
          Something went wrong. Please send Christian the following information:
        </IgTypography>
      </Grid>
      <Grid item>
        <ReactJson src={compareEvents || {}} />
      </Grid>
      <Grid item>
        <IgTypography>
          <pre>{JSON.stringify(error, undefined, 2)}</pre>
        </IgTypography>
      </Grid>
    </Grid>
  );
};
export const ConfigCompareDomain = () => {
  const org = useGetOrg();
  const [filters, dispatchFilters] = useImmerReducer<
    FilterReduceState,
    FilterReducerAction
  >(filterReducer, {
    value: "",
    startTime: null,
    endTime: null,
  });

  const configEvents = useGetConfigEvents({
    startTs: filters.startTime?.toSeconds(),
    endTs: filters.endTime?.toSeconds(),
  });

  const [compareEvents, setCompareEvents] = useImmer<CompareEvents>(null);

  const eventsWithDate: ConfigEventType[] = useMemo(() => {
    return configEvents.data.events.map((event) => ({
      ...event,
      date: DateTime.fromSeconds(event.ts).toLocaleString(
        DateTime.DATETIME_MED_WITH_SECONDS
      ),
    }));
  }, [configEvents.data?.events]);

  const prefetchConfigCompares = useConfigCompareQueries(
    eventsWithDate.map((event, i, e) => ({
      from: e[i + 1]?.key,
      to: e[i].key,
    }))
  );

  const events = useMemo(() => {
    const filteredEvents: ConfigEventType[] = [];
    for (const [i, event] of eventsWithDate.entries()) {
      const prefetchedData = prefetchConfigCompares[i];
      if (!filters.value || !prefetchedData.isSuccess) {
        filteredEvents.push(event);
      } else {
        const filteredEventData = filterByExperience(
          filters.value,
          prefetchedData.data!
        );

        if (Object.keys(filteredEventData).length) {
          filteredEvents.push(event);
        }
      }
    }

    return filteredEvents;
  }, [eventsWithDate, prefetchConfigCompares]);

  useEffect(() => {
    setCompareEvents({
      from: configEvents.data.events[1],
      to: configEvents.data.events[0],
    });
  }, [configEvents.data.events]);

  if (!compareEvents) {
    return <Loading />;
  }

  return (
    <Grid container direction={"column"} rowSpacing={2}>
      <Title />
      <Grid container item direction={"row"} columnSpacing={2}>
        <Grid item xs={2.3}>
          <IgPaper
            sx={{
              height: "100%",
              padding: "20px 10px 20px 10px",
              minWidth: "198px",
              minHeight: "500px",
              maxHeight: "600px",
            }}
          >
            {events.length ? (
              <Grid
                container
                item
                justifyContent={"center"}
                sx={{ maxHeight: "590px", overflow: "scroll" }}
              >
                <Grid item>
                  <IgTypography sx={{ fontWeight: 600 }}>
                    Config History
                  </IgTypography>
                </Grid>
                <Grid item>
                  <List>
                    {events.map((event, i, events) => (
                      <ListItem disablePadding key={event.ts}>
                        <ListItemButton
                          selected={compareEvents?.to?.key === event.key}
                          onClick={() => {
                            setCompareEvents({
                              to: events[i],
                              from: events[i + 1],
                            });
                          }}
                        >
                          <Box component={"span"}>
                            <IgTypography
                              sx={{ fontSize: "14px", marginRight: "10px" }}
                            >
                              {event.date}
                            </IgTypography>
                          </Box>
                          {prefetchConfigCompares[i].isLoading ? (
                            <CircularProgress size={12} />
                          ) : null}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                item
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Grid item textAlign={"center"}>
                  <IgTypography>No Events</IgTypography>
                </Grid>
              </Grid>
            )}
          </IgPaper>
        </Grid>
        <Grid container item xs={9.7} direction={"column"} rowSpacing={1}>
          <Grid item>
            <EventFilter filters={filters} dispatchFilters={dispatchFilters} />
          </Grid>
          <Grid item>
            <IgPaper sx={{ padding: "20px 10px 20px 10px" }}>
              <Grid
                container
                item
                columnSpacing={2}
                flexWrap={"nowrap"}
                sx={{ minHeight: "390px" }}
                direction={"row"}
              >
                {configEvents.isSuccess && !events.length ? (
                  <Grid
                    container
                    item
                    justifyContent={"center"}
                    alignItems={"center"}
                  >
                    <IgTypography>
                      No events found in this interval
                    </IgTypography>
                  </Grid>
                ) : (
                  <Grid container item>
                    <ErrorBoundary
                      FallbackComponent={(props) =>
                        Fallback({ ...props, compareEvents: compareEvents })
                      }
                    >
                      <EventPanel
                        compareEvents={compareEvents}
                        filters={filters}
                      />
                    </ErrorBoundary>
                  </Grid>
                )}
              </Grid>
            </IgPaper>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
